import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, UserMinus, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Friends & Favourites panel. Lives inside the game lobby above the LFS panel.
 * Shows saved friends and lets you invite any of them to the current game in
 * one click.
 */
export const FriendsPanel = ({ game, user, disabled, refreshKey, onFriendsLoaded }) => {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingInviteTo, setSendingInviteTo] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fetchFriends = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/friends`);
      setFriends(data.friends || []);
      if (onFriendsLoaded) onFriendsLoaded(data.friends || []);
    } catch (e) {
      logger.error('Failed to fetch friends', e);
    } finally {
      setLoading(false);
    }
  }, [onFriendsLoaded]);

  useEffect(() => {
    fetchFriends();
  }, [fetchFriends, refreshKey]);

  const inviteFriend = async (friend) => {
    if (!game?.id) return;
    setSendingInviteTo(friend.user_id);
    try {
      await axios.post(`${API}/invites/send`, {
        to_user_id: friend.user_id,
        game_type: game.id,
      });
      toast.success(`Invite sent to ${friend.username}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInviteTo(null);
    }
  };

  const removeFriend = async (friend) => {
    setRemovingId(friend.user_id);
    try {
      await axios.post(`${API}/friends/remove`, { friend_user_id: friend.user_id });
      setFriends((prev) => prev.filter((f) => f.user_id !== friend.user_id));
      toast.message(`Removed ${friend.username} from friends`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove friend');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg p-5 w-full max-w-md mx-auto"
        data-testid="friends-panel-loading"
      >
        <p className="text-sm text-[#71717A] text-center">Loading friends…</p>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-lg p-5 w-full max-w-md mx-auto"
      data-testid="friends-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart size={20} weight="fill" className="text-[#FF3366]" />
          <h3 className="font-heading text-base font-bold text-[#09090B]">
            Favourites
          </h3>
          <span
            className="text-xs text-[#71717A] font-mono"
            data-testid="friends-count"
          >
            {friends.length}
          </span>
        </div>
      </div>

      {friends.length === 0 ? (
        <p
          className="text-sm text-[#71717A] text-center py-4"
          data-testid="friends-empty"
        >
          No favourites yet. Invites you accept are saved here automatically.
        </p>
      ) : (
        <div className="space-y-2" data-testid="friends-list">
          {friends.map((f) => (
            <div
              key={f.user_id}
              className="flex items-center justify-between gap-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded px-3 py-2"
              data-testid={`friend-row-${f.user_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={f.avatar} alt={f.username} />
                  <AvatarFallback>{f.username?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#09090B] truncate">
                    {f.username}
                  </p>
                  <p className="text-[10px] text-[#71717A] font-mono uppercase tracking-wider">
                    {f.score} pts
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  onClick={() => inviteFriend(f)}
                  disabled={sendingInviteTo === f.user_id || disabled || !game?.id}
                  size="sm"
                  className="h-7 px-2 bg-[#0047FF] hover:bg-[#0037CC] text-white"
                  data-testid={`invite-friend-${f.user_id}`}
                  title={!game?.id ? 'Select a game first' : `Invite to ${game.name}`}
                >
                  {sendingInviteTo === f.user_id ? (
                    '…'
                  ) : (
                    <>
                      <PaperPlaneTilt size={12} weight="fill" className="mr-1" />
                      Invite
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => removeFriend(f)}
                  disabled={removingId === f.user_id}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-[#71717A] hover:bg-[#F4F4F5] hover:text-[#FF3333]"
                  data-testid={`remove-friend-${f.user_id}`}
                  aria-label={`Remove ${f.username} from favourites`}
                >
                  <UserMinus size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
